import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Users, ClipboardCheck, TrendingDown, Percent } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";

const StatCard = ({ Icon, label, value, color = "#005BBB" }) => (
  <div className="brutal p-5 bg-white flex items-center gap-4">
    <div className="w-14 h-14 rounded-2xl border-2 border-black flex items-center justify-center" style={{ background: color }}>
      <Icon className="w-7 h-7 text-white" />
    </div>
    <div>
      <div className="text-xs font-display font-bold uppercase tracking-widest text-neutral-600">
        {label}
      </div>
      <div className="font-display font-black text-3xl leading-none mt-1">{value}</div>
    </div>
  </div>
);

export default function Dashboard() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/dashboard");
      setData(data);
    })();
  }, []);

  if (!data)
    return <div className="font-display">Loading dashboard…</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <h1 className="font-display font-black text-4xl">{t("dashboard")}</h1>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard Icon={ClipboardCheck} label={t("totalExams")} value={data.total_exams} color="#005BBB" />
        <StatCard Icon={Users} label={t("totalAttempts")} value={data.total_attempts} color="#E3000F" />
        <StatCard Icon={Percent} label={t("avgScore")} value={`${data.avg_score}%`} color="#0B0B14" />
        <StatCard Icon={Trophy} label={t("ranking")} value={data.top_employees.length} color="#FFB800" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="brutal p-6 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-5 h-5 text-canales-red" />
            <h3 className="font-display font-black text-xl">{t("ranking")}</h3>
          </div>
          {data.top_employees.length === 0 ? (
            <p className="font-body text-neutral-600">{t("noAttempts")}</p>
          ) : (
            <ol className="space-y-2">
              {data.top_employees.map((e, i) => (
                <li key={e.employee_name + i} className="flex justify-between items-center brutal-sm px-3 py-2 bg-canales-cream">
                  <span className="font-display font-bold">
                    #{i + 1} · {e.employee_name}
                  </span>
                  <span className="font-body text-sm">
                    {e.avg}% · {e.attempts} {lang === "es" ? "intentos" : "attempts"}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="brutal p-6 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="w-5 h-5 text-canales-red" />
            <h3 className="font-display font-black text-xl">{t("topFails")}</h3>
          </div>
          {data.top_failed_questions.length === 0 ? (
            <p className="font-body text-neutral-600">{t("noAttempts")}</p>
          ) : (
            <ul className="space-y-2">
              {data.top_failed_questions.map((q) => (
                <li key={q.question_id} className="brutal-sm p-3 bg-red-50">
                  <div className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">
                    {q.topic} · {q.fails} {lang === "es" ? "fallas" : "fails"}
                  </div>
                  <div className="font-body">{q[`prompt_${lang}`]}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </motion.div>
  );
}
